/**
 * Tipos de dominio del feature "Escuchar & Hablar" (speak Fase 19.D).
 *
 * Reflejan 1:1 los shapes que devuelve `apps/backend/src/routes/listen.ts`
 * (catálogo + capítulo + progreso) más los tipos derivados que consume la UI
 * (Layout del karaoke, estados de capítulo). NO inventan campos: lo que el
 * endpoint no manda, no existe acá.
 */

import { formatOrbsLabel } from '@orborix/shared-types';

export type Cefr = 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
export type Edition = 'short' | 'long';

/** Orden pedagógico de los niveles (selector + fallback). */
export const CEFR_ORDER: readonly Cefr[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/** Los 5 estados de un capítulo en la biblioteca. Rankeados (never-downgrade).
 *  "repasado" (19.G) = completó una actividad de repaso post-capítulo. */
export type ChapterStatus = 'no-escuchado' | 'escuchando' | 'escuchado' | 'repasado' | 'conversado';

/** Rango numérico para comparar estados (el server ya es never-downgrade; el
 *  cliente sólo deriva de `listenedAt`/`reviewedAt`/`conversedAt`, así que no
 *  degrada solo). */
export const STATUS_RANK: Record<ChapterStatus, number> = {
  'no-escuchado': 0,
  escuchando: 1,
  escuchado: 2,
  repasado: 3,
  conversado: 4,
};

// ─── Catálogo (`GET /api/listen/catalog`) ───────────────────────────────────

export interface CatalogKeypoint {
  slug: string;
  order: number;
  title: string;
  titleEs: string | null;
  keyIdea: string;
  durationTargetMin: number | null;
  /** Niveles CEFR con rendition disponible (para el selector + "is-locked"). */
  levels: Cefr[];
  /** Idiomas con rendition disponible. */
  langs: string[];
  /** Fase 22: el user ya puede escucharlo sin pagar (sample, comprado, grandfather). */
  unlocked: boolean;
  /** Fase 22: precio del unlock en nano-USD ("0" si unlocked). Calculado server-side. */
  priceNano: string;
}

export interface CatalogEdition {
  keypoints: CatalogKeypoint[];
}

export interface CatalogBook {
  slug: string;
  title: string;
  author: string;
  lang: string;
  description: string | null;
  coverUrl: string | null;
  editions: Partial<Record<Edition, CatalogEdition>>;
  /**
   * Acentos de la narración EN INGLÉS del libro (`us` / `uk`), de
   * `listen_renditions.accent`. La Audioteca muestra el de cada tapa y pone
   * primero los de «Tu inglés» (encargo 111). No confundir con `accent`, que es
   * el color de la cover.
   */
  narrationAccents: string[];
  // ─── Metadata de presentación (Workstream A — covers generativas) ─────────
  // Opcionales: si el backend todavía no las expone, la UI las deriva del slug
  // (ver `deriveCoverMeta`). Cuando 19.A las agrega al `/catalog`, mandan ellas.
  /** Color base de la cover generativa (hex/oklch). */
  accent?: string | null;
  /** Patrón de la cover (`orbit|waves|bloom|strata|split|loop`). */
  motif?: string | null;
  /** Categoría editorial (chip). */
  category?: string | null;
  /** Frase corta de presentación (≈ description). */
  blurb?: string | null;
  /** Destacado en la biblioteca. */
  featured?: boolean | null;
}

// ─── Capítulo (`GET /api/listen/chapter/...`) ───────────────────────────────

export interface Rendition {
  slug: string;
  cefr: Cefr;
  lang: string;
  accent: string | null;
  gender: string | null;
  voiceSlug: string;
  /**
   * URL del audio. Dev: ruta local (`/listen/assets/...`) con Range; prod:
   * URL S3 firmada EFÍMERA (~10 min). `null` sólo cuando el capítulo vino
   * `locked` (Fase 22) — el provider desbloquea antes de reproducir, así que
   * una rendition activa siempre tiene URL.
   */
  audioUrl: string | null;
  alignmentUrl: string | null;
  durationSec: number | null;
}

/** Una combinación (cefr, lang, voz) que SÍ existe para el capítulo. */
export interface Availability {
  cefr: Cefr;
  lang: string;
  /** Acento de la narración (`us` / `uk` / `latam`). */
  accent: string | null;
  voiceSlug: string;
}

export interface ChapterProgress {
  lastPositionSec: number;
  lastCefr: Cefr | null;
  /** ISO8601 o null. */
  listenedAt: string | null;
  reviewedAt: string | null;
  conversedAt: string | null;
}

export interface ChapterResponse {
  book: string;
  edition: Edition;
  keypoint: string;
  /** Fase 22: el capítulo requiere unlock pago — `rendition.audioUrl` viene null. */
  locked: boolean;
  /** Precio del unlock en nano-USD ("0" si no está locked). */
  priceNano: string;
  rendition: Rendition;
  available: Availability[];
  progress: ChapterProgress | null;
}

// ─── Progreso (`GET /api/listen/progress`) ──────────────────────────────────

export interface ProgressEntry {
  book: string;
  edition: Edition;
  keypoint: string;
  lastPositionSec: number;
  lastCefr: Cefr | null;
  listenedAt: string | null;
  reviewedAt: string | null;
  conversedAt: string | null;
  /** ISO del último avance (para ordenar "seguí escuchando" por recencia). */
  updatedAt: string | null;
  /**
   * Duración REAL del mp3 (rendition del `lastCefr`), para pos/total en el
   * Home sin abrir el player. `null` = desconocida (sin cefr registrado o sin
   * rendition/duración a ese nivel) — la UI muestra sólo la posición.
   */
  durationSec: number | null;
}

// ─── Layout del karaoke (derivado de `alignment.json`) ──────────────────────

export interface KaraokeWord {
  text: string;
  start: number;
  end: number;
}

export interface KaraokeLine {
  /** Índice de línea (== índice de sentence). */
  li: number;
  text: string;
  start: number;
  end: number;
  words: KaraokeWord[];
}

export interface KaraokeLayout {
  duration: number;
  lines: KaraokeLine[];
}

// ─── Referencia a un capítulo (clave de progreso / hint al coach) ───────────

export interface ChapterRef {
  book: string;
  edition: Edition;
  keypoint: string;
}

/** Clave canónica de un capítulo (índice de `statuses`/progreso). */
export function chapterKey(ref: ChapterRef): string {
  return `${ref.book}|${ref.edition}|${ref.keypoint}`;
}

/**
 * Formatea un precio en nano-USD como **orbs** para el badge/toast de unlock
 * (Fase 22): `3_600_000` → `"36 orbs"`, `1_715_000` → `"17 orbs"`. Orbs es la
 * moneda virtual que ve el usuario (1 orb = $0,001 = 100_000 nano); la tasa
 * vive en `@orborix/shared-types` (única fuente). El monto SIEMPRE viene del
 * backend en nano — acá sólo se denomina en orbs para mostrar. Devuelve "" si
 * el monto es inválido (el call site igual gatea por `priceNano !== '0'`).
 */
export function formatPrice(priceNano: string): string {
  try {
    return formatOrbsLabel(priceNano);
  } catch {
    return '';
  }
}
